import { ReviewScore } from "../../model/datasource/score";
import { scaleLinear, ScaleLinear, scaleOrdinal, ScaleOrdinal } from "d3-scale";
import { BaseType, select, Selection } from "d3-selection";
import { Series as _Series, SeriesPoint, stack } from "d3-shape";

interface Dimensions {
	width: number;
	height: number;
	padding: {
		top: number;
		left: number;
		bottom: number;
		right: number;
	};
	totalWidth: number;
	totalHeight: number;
}

type Series = _Series<ReviewScore, string>;
type Datum = SeriesPoint<ReviewScore> & { key?: string };
type Chart = Selection<SVGElement, unknown, HTMLElement, unknown>;
type Popup = Selection<HTMLElement, unknown, HTMLElement, unknown>
type Segments = Selection<BaseType, Datum, BaseType, Series>;
type Percents = Selection<BaseType, Datum, BaseType, Series>;

interface Axes {
	x: ScaleLinear<number, number>;
	background: ScaleOrdinal<string, string>;
	title: ScaleOrdinal<string, string>;
}

export default class ReviewTotalScoreChart {
	categories: string[];
	colors: string[];
	dimensions: Dimensions;

	constructor(categories: string[], colors: string[], width: number, height: number, padding: number) {
		this.categories = categories || ['correct', 'incorrect', 'unanswered'];
		this.colors = colors || ['#28a745', '#dc3545', '#17a2b8'];
		this.dimensions = this.getDimensions(width, height, padding, padding, padding, padding);
	}

	display(element: Element, total: ReviewScore): void {
		const { background, title, x } =
			this.axes(total, this.categories, this.colors, this.dimensions);
		const data = this.formatData(total, this.categories);

		const chart = this.addChart(element.querySelector<HTMLElement>('svg'), this.dimensions);
		const tooltip = this.addTooltip(element);
		const bars = this.addBars(chart, data, background);

		this.addEvents(bars, tooltip, element, title, total.total);
		this.fillBars(bars, x, this.dimensions);
		this.addPercent(bars, x, total.total, this.dimensions.height);
	}

	getDimensions(width: number, height: number, padTop: number, padLeft: number, padBottom: number, padRight: number): Dimensions {
		return {
			width: width,
			height: height,
			padding: { top: padTop, left: padLeft, bottom: padBottom, right: padRight },
			totalWidth: width + padLeft + padRight,
			totalHeight: height + padTop + padBottom
		};
	}

	axes(total: ReviewScore, categories: string[], colors: string[], dimensions: Dimensions): Axes {
		return {
			background: scaleOrdinal<string>()
				.domain(categories)
				.range(colors),
			title: scaleOrdinal<string>()
				.domain(categories)
				.range(['Correct', 'Incorrect', 'Unanswered']),
			x: scaleLinear()
				.domain([0, total.total])
				.range([0, dimensions.width])
		};
	}

	formatData(total: ReviewScore, categories: string[]): Series[] {
		return stack<ReviewScore>()
			.keys(categories)
			([total]);
	}

	addChart(element: HTMLElement, dimensions: Dimensions): Chart {
		const svg = select(element)
			.attr('width', dimensions.totalWidth + '%')
			.attr('height', dimensions.totalHeight);
		return svg.append('g')
			.attr('transform', 'translate(' +
				dimensions.padding.left + ',' +
				dimensions.padding.top +
				')');
	}

	addTooltip(parent: Element): Popup {
		return select(parent)
			.insert('div', ':first-child')
				.style('float', 'left')
				.style('width', 0)
			.insert('div', ':first-child')
				.attr('class', 'border rounded p-1 bg-light')
				.style('width', 'max-content')
				.style('display', 'none')
				.style('z-index', 10)
				.style('position', 'relative');
	}

	addBars(chart: Chart, data: Series[], background: ScaleOrdinal<string, string>): Segments {
		return chart.selectAll('g')
			.data(data.reverse())
			.join('g')
				.attr('fill', d => background(d.key))
			.selectAll('g')
			.data(d => d.map((i: Datum) => {
				i.key = d.key;
				return i;
			}))
			.join('g');
	}

	addEvents(bars: Segments, tooltip: Popup, parent: Element, title: ScaleOrdinal<string, string>, total: number): Segments {
		return bars.on('mouseover', (e: MouseEvent, d: Datum) => {
			select(e.currentTarget as Element).style('opacity', 0.9);
			tooltip.style('display', 'block');
		})
			.on('mousemove', (e: MouseEvent, d: Datum) => {
				const chartRect = parent.getBoundingClientRect();
				tooltip.style('top', (
					e.pageY - chartRect.y - window.scrollY
				) + 'px')
					.style('left', (
						e.pageX - chartRect.x - window.scrollX + 20
					) + 'px')
					.html(title(d.key)+': ' + d.data[d.key] + ' / ' + total);
			})
			.on('mouseout', (e: MouseEvent, d: Datum) => {
				select(e.currentTarget as Element).style('opacity', 1);
				tooltip.style('display', 'none');
			});
	}

	fillBars(bars: Segments, x: ScaleLinear<number, number>, dimensions: Dimensions): Segments {
		return bars.append("rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("rx", 5)
			.attr("width", d => x(d[1]) + '%')
			.attr("height", dimensions.height);
	}

	addPercent(bars: Segments, x: ScaleLinear<number, number>, total: number, height: number): Percents {
		return bars.append('text')
			.attr('x', d => x((d[1] - d[0]) / 2 + d[0]) + '%')
			.attr('dy', '50%')
			.attr('dominant-baseline', 'central')
			.attr('text-anchor', 'middle')
			.attr('fill', 'white')
			.text(d => (d[1] - d[0]) / total < 0.04 ?
				'' :
				Math.round(d.data[d.key] / total * 100) + '%'
			);
	}
}
